// frontend/packages/business-console/src/pages/SiteAlerts.jsx
//
// Site alerts: post an urgent banner across the top of the tenant's website
// (closure, weather delay, boil-water notice). Alerts do not wait on a site
// rebuild: the site's banner reads them at runtime, so a posted or ended
// alert reaches visitors within about a minute.
//
// Self-gates on GET /v2/site-alerts/status (the emergency_alerts tenant
// feature), with a friendly "not turned on" state for tenants without it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  getSiteAlertsStatus,
  listSiteAlerts,
  createSiteAlert,
  updateSiteAlert,
  endSiteAlert,
  reactivateSiteAlert,
  deleteSiteAlert,
} from '../services/siteAlertsService';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  useTokens,
  PageHeader,
  Surface,
  StatusPill,
} from '../components/primitives';

const LEVELS = [
  {
    key: 'emergency',
    label: 'Emergency',
    hint: 'Closures, lockdowns, safety issues',
    pill: 'error',
    bg: '#b91c1c',
    fg: '#ffffff',
  },
  {
    key: 'warning',
    label: 'Warning',
    hint: 'Delays, early release, weather',
    pill: 'warning',
    bg: '#fbbf24',
    fg: '#111827',
  },
  {
    key: 'info',
    label: 'Notice',
    hint: 'Reminders and general updates',
    pill: 'info',
    bg: '#1d4ed8',
    fg: '#ffffff',
  },
];
const LEVEL = Object.fromEntries(LEVELS.map((l) => [l.key, l]));

const EMPTY = {
  level: 'emergency',
  title: '',
  message: '',
  linkUrl: '',
  linkLabel: '',
  endsAt: '',
};

// <input type="datetime-local"> wants local "YYYY-MM-DDTHH:mm".
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isShowing(a, now = Date.now()) {
  return (
    a.status === 'active' &&
    new Date(a.startsAt).getTime() <= now &&
    (!a.endsAt || new Date(a.endsAt).getTime() > now)
  );
}

function BannerPreview({ alert }) {
  const lvl = LEVEL[alert.level] || LEVEL.warning;
  return (
    <Box
      role="img"
      aria-label="Preview of the banner on your website"
      sx={{
        bgcolor: lvl.bg,
        color: lvl.fg,
        borderRadius: '10px',
        px: 2,
        py: 1.25,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: 1,
        fontSize: 14,
      }}
    >
      <Box component="strong" sx={{ fontWeight: 800 }}>
        {alert.title || 'Your alert title'}
      </Box>
      {alert.message && <span>{alert.message}</span>}
      {alert.linkUrl && (
        <Box
          component="span"
          sx={{ textDecoration: 'underline', fontWeight: 700 }}
        >
          {alert.linkLabel || 'Learn more'}
        </Box>
      )}
    </Box>
  );
}

export default function SiteAlerts() {
  const t = useTokens();
  const { showSnackbar } = useSnackbar();

  const [gate, setGate] = useState({ loading: true, enabled: false });
  const [alerts, setAlerts] = useState([]);
  const [listError, setListError] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      setAlerts(await listSiteAlerts());
    } catch (err) {
      setListError(err?.response?.data?.error || 'Could not load your alerts.');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const status = await getSiteAlertsStatus();
      if (!alive) return;
      setGate({ loading: false, enabled: Boolean(status?.enabled) });
      if (status?.enabled) load();
    })();
    return () => {
      alive = false;
    };
  }, [load]);

  const { showing, past } = useMemo(() => {
    const now = Date.now();
    return {
      showing: alerts.filter(
        (a) =>
          isShowing(a, now) ||
          (a.status === 'active' && new Date(a.startsAt).getTime() > now)
      ),
      past: alerts.filter(
        (a) =>
          !(
            isShowing(a, now) ||
            (a.status === 'active' && new Date(a.startsAt).getTime() > now)
          )
      ),
    };
  }, [alerts]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const payload = () => ({
    level: form.level,
    title: form.title.trim(),
    message: form.message.trim() || null,
    linkUrl: form.linkUrl.trim() || null,
    linkLabel: form.linkLabel.trim() || null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
  });

  const onSubmit = () => {
    if (!form.title.trim()) {
      setFormError(
        'Add a short title, for example "All campuses closed today".'
      );
      return;
    }
    setFormError(null);
    if (editingId) save();
    else setConfirmOpen(true);
  };

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      if (editingId) {
        await updateSiteAlert(editingId, payload());
        showSnackbar(
          'Alert updated. Your website shows the change within a minute.',
          'success'
        );
      } else {
        await createSiteAlert(payload());
        showSnackbar(
          'Alert posted. It appears on your website within a minute.',
          'success'
        );
      }
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (err) {
      setFormError(
        err?.response?.data?.error || 'Could not save the alert. Try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      level: a.level,
      title: a.title || '',
      message: a.message || '',
      linkUrl: a.linkUrl || '',
      linkLabel: a.linkLabel || '',
      endsAt: toLocalInput(a.endsAt),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const act = async (id, fn, msg) => {
    setBusyId(id);
    try {
      await fn(id);
      showSnackbar(msg, 'success');
      await load();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || 'That did not work. Try again.',
        'error'
      );
    } finally {
      setBusyId(null);
    }
  };

  if (gate.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (!gate.enabled) {
    return (
      <Box>
        <PageHeader title="Site alerts" sx={{ mb: 2 }} />
        <Surface sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Site alerts are not turned on for your website yet.
          </Typography>
          <Typography sx={{ color: t.muted, fontSize: 14 }}>
            Site alerts put an urgent banner across the top of every page of
            your website in under a minute, with no rebuild. Contact us at
            support@upryzze.com to turn it on.
          </Typography>
        </Surface>
      </Box>
    );
  }

  const row = (a, live) => {
    const lvl = LEVEL[a.level] || LEVEL.warning;
    const scheduled =
      a.status === 'active' && new Date(a.startsAt).getTime() > Date.now();
    return (
      <Surface key={a.id} sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5, flexWrap: 'wrap' }}
            >
              <StatusPill kind={lvl.pill} label={lvl.label} size="sm" />
              {live && !scheduled && (
                <StatusPill kind="success" label="Showing now" dot size="sm" />
              )}
              {scheduled && (
                <StatusPill
                  kind="neutral"
                  label={`Starts ${fmt(a.startsAt)}`}
                  size="sm"
                />
              )}
            </Stack>
            <Typography sx={{ fontWeight: 800 }}>{a.title}</Typography>
            {a.message && (
              <Typography sx={{ fontSize: 14, color: t.textSecondary }}>
                {a.message}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: t.muted, mt: 0.5 }}>
              Posted {fmt(a.createdAt)}
              {live && a.endsAt ? `, ends automatically ${fmt(a.endsAt)}` : ''}
              {!live &&
                (a.endedAt
                  ? `, ended ${fmt(a.endedAt)}`
                  : a.endsAt
                    ? `, ended ${fmt(a.endsAt)}`
                    : '')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {live ? (
              <>
                <Button
                  size="small"
                  onClick={() => startEdit(a)}
                  disabled={busyId === a.id}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={busyId === a.id}
                  onClick={() =>
                    act(
                      a.id,
                      endSiteAlert,
                      'Alert ended. It leaves your website within a minute.'
                    )
                  }
                >
                  End alert
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  disabled={busyId === a.id}
                  onClick={() =>
                    act(a.id, reactivateSiteAlert, 'Alert posted again.')
                  }
                >
                  Post again
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  disabled={busyId === a.id}
                  onClick={() =>
                    act(a.id, deleteSiteAlert, 'Alert removed from this list.')
                  }
                >
                  Remove
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Surface>
    );
  };

  return (
    <Box>
      <PageHeader
        title="Site alerts"
        meta={
          <Typography sx={{ color: t.muted, fontSize: 14 }}>
            An urgent banner across the top of every page of your website. Shows
            within a minute, no rebuild.
          </Typography>
        }
        sx={{ mb: 2.5 }}
      />

      <Surface sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography
          component="h2"
          sx={{ fontWeight: 800, fontSize: 17, mb: 2 }}
        >
          {editingId ? 'Edit alert' : 'Post an alert'}
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}
              id="alert-level-label"
            >
              How urgent is it?
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={form.level}
              onChange={(_, v) => v && setForm((f) => ({ ...f, level: v }))}
              aria-labelledby="alert-level-label"
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              {LEVELS.map((l) => (
                <ToggleButton
                  key={l.key}
                  value={l.key}
                  sx={{ textTransform: 'none', px: 2 }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Box sx={{ fontWeight: 800 }}>{l.label}</Box>
                    <Box sx={{ fontSize: 11, color: t.muted }}>{l.hint}</Box>
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TextField
            label="Title"
            value={form.title}
            onChange={set('title')}
            placeholder="All campuses closed today, Tuesday, January 12"
            inputProps={{ maxLength: 160 }}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Details (optional)"
            value={form.message}
            onChange={set('message')}
            placeholder="Icy roads. All after-school activities are canceled."
            inputProps={{ maxLength: 1000 }}
            multiline
            minRows={2}
            fullWidth
            size="small"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Link (optional)"
              value={form.linkUrl}
              onChange={set('linkUrl')}
              placeholder="/news/weather-closure/ or https://..."
              helperText="A page on your site or a full https:// link"
              fullWidth
              size="small"
            />
            <TextField
              label="Link text"
              value={form.linkLabel}
              onChange={set('linkLabel')}
              placeholder="Read more"
              inputProps={{ maxLength: 60 }}
              size="small"
              sx={{ minWidth: { sm: 200 } }}
            />
          </Stack>
          <TextField
            label="End automatically (optional)"
            type="datetime-local"
            value={form.endsAt}
            onChange={set('endsAt')}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to keep it up until you end it"
            size="small"
            sx={{ maxWidth: 320 }}
          />

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
              Preview
            </Typography>
            <BannerPreview alert={form} />
          </Box>

          {formError && <Alert severity="error">{formError}</Alert>}

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              color={form.level === 'emergency' ? 'error' : 'primary'}
              onClick={onSubmit}
              disabled={saving}
            >
              {saving
                ? 'Saving...'
                : editingId
                  ? 'Save changes'
                  : 'Post alert now'}
            </Button>
            {editingId && (
              <Button
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY);
                  setFormError(null);
                }}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </Surface>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      <Typography
        component="h2"
        sx={{ fontWeight: 800, fontSize: 17, mb: 1.5 }}
      >
        On your website now
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {showing.length === 0 ? (
          <Typography sx={{ color: t.muted, fontSize: 14 }}>
            No alerts are showing.
          </Typography>
        ) : (
          showing.map((a) => row(a, true))
        )}
      </Stack>

      {past.length > 0 && (
        <>
          <Typography
            component="h2"
            sx={{ fontWeight: 800, fontSize: 17, mb: 1.5 }}
          >
            Past alerts
          </Typography>
          <Stack spacing={1.5}>{past.map((a) => row(a, false))}</Stack>
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Post this alert to your website?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: 14, color: t.textSecondary }}>
            It appears across the top of every page within a minute and stays up
            until you end it
            {form.endsAt
              ? ` or ${fmt(new Date(form.endsAt).toISOString())}`
              : ''}
            .
          </Typography>
          <BannerPreview alert={form} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={form.level === 'emergency' ? 'error' : 'primary'}
            onClick={save}
          >
            Post alert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
